<script lang="ts">
import { classname } from '@/js/utils.js';
import { computed } from 'vue';

const classBaseInputRadio = classname('base-input-radio');
export default {
  name: 'BaseInputRadio',
  inheritAttrs: false,
  props: {
    name: {
      type: String,
      default: null,
    },
    value: {
      type: String,
      default: null,
    },
    modelValue: {
      type: String,
      default: null,
    },
    size: {
      type: String,
      default: null,
    },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const emitCheck = () => {
      emit('update:modelValue', props.value);
    };
    const checked = computed(() => props.value === props.modelValue);
    return {
      classBaseInputRadio,
      emitCheck,
      checked,
    };
  },
};
</script>

<template>
  <label :class="classBaseInputRadio({ checked, size })">
    <input
      v-bind="$attrs"
      :value="value"
      :checked="checked"
      :name="name"
      type="radio"
      @change="emitCheck"
    >
    <span :class="classBaseInputRadio('items')">
      <span :class="classBaseInputRadio('item')">
        <slot name="default" />
      </span>
      <span :class="classBaseInputRadio('item', { type: 'price' })"><slot name="subtitle" /></span>
      <span :class="classBaseInputRadio('item', { type: 'store' })"><slot name="additional" /></span>
    </span>
  </label>
</template>

<style lang="scss">
.base-input-radio {
  --border: #c4c4c4;
  --focus: #3475dd4c;
  --active: #3962b2;
  --disabled: #f6f8ff;
  --border-hover: #3962b2;
  --size: 30px;

  display: inline-flex;
  grid-gap: 11px;
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  margin: 0;
  padding: 22px;
  font-size: 18px;
  border: 1px solid #c4c4c4;
  border-radius: 2px;
  transition:
    color 0.3s,
    background-color 0.3s,
    border-color 0.3s;

  &_checked {
    background-color: #3962b319;
    border-color: #3962b3;
  }

  &__items {
    display: grid;
    margin-top: 2px;
  }

  &__item {
    display: block;

    &_type {
      &_price {
        font-size: 16px;
      }

      &_store {
        color: #c4c4c4;
        font-size: 16px;
      }
    }
  }

  input {
    position: relative;
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    width: var(--size);
    height: var(--size);
    margin: 0;
    border: 1px solid var(--bc, var(--border));
    border-radius: 1px;
    transition:
      background-color 0.3s,
      border-color 0.3s,
      box-shadow 0.2s;
    -webkit-appearance: none;
    -moz-appearance: none;

    &:focus {
      outline-color: var(--focus);

      &::placeholder {
        opacity: 0.7;
      }
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }

    &:hover {
      &:not(:disabled) {
        --bc: var(--border-hover);
      }
    }

    &[type='radio'] {
      border-radius: 50%;

      &::after {
        position: absolute;
        display: block;
        width: calc(var(--size) / 2);
        height: calc(var(--size) / 2);
        background: #fff;
        border-radius: 50%;
        opacity: 0;
        transition:
          transform var(--d-t, 0.3s) var(--d-t-e, ease),
          opacity var(--d-o, 0.2s);
        content: '';
      }

      &:checked {
        background-color: var(--active);
        border-color: var(--active);

        &::after {
          opacity: 1;
        }
      }
    }
  }

  &_size {
    &_sm {
      --size: 20px;

      padding: 8px 13px 10px 11px;
      font-size: 16px;
    }
  }

  @media screen and (max-width: 991px) {
    padding: 8px;
    font-size: 14px;
  }

  @media screen and (max-width: 500px) {
    &__items {
      margin-top: 0;
    }

    &__item {
      &_type {
        &_price {
          font-size: 14px;
        }

        &_store {
          font-size: 14px;
        }
      }
    }
  }
}
</style>
